'use client';

import { useQuery } from '@tanstack/react-query';

/** One speaker attached to a landing page event card. */
export interface LandingSpeaker {
  id: string;
  name: string;
  title: string | null;
  photoUrl: string | null;
  displayOrder: number;
}

/**
 * Fields of a blog event consumed by the landing page EventSection. The
 * featured image (16:9 banner) fills the carousel card imagery while the
 * thumbnail (3:4 portrait) is used only as a fallback; speakers power the
 * compact speaker line.
 */
export interface LandingEvent {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featuredImage: string | null;
  thumbnailImage: string | null;
  eventMode: string;
  venueName: string | null;
  venueCity: string | null;
  startAt: string;
  endAt: string | null;
  timezone: string;
  isHighlighted: boolean;
  speakers: LandingSpeaker[];
}

/** Response envelope of the public events listing (/api/blog/events). */
interface EventsResponse {
  data: LandingEvent[];
}

/** Upper bound on the number of upcoming events loaded into the carousel. */
const CAROUSEL_LIMIT = 12;

/**
 * Sets up the three loading queries of the landing page carousel: the
 * highlighted event, the upcoming events, and every past event. A future
 * highlighted event pins first; everything else sorts by most recent
 * startAt first.
 */
export function useLandingEvents() {
  const featuredQuery = useQuery<EventsResponse>({
    queryKey: ['blog-events', 'featured', 'landing'],
    queryFn: async () => {
      const res = await fetch('/api/blog/events?highlighted=1');
      if (!res.ok) throw new Error('Failed to fetch featured event');
      return res.json();
    },
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  const upcomingQuery = useQuery<EventsResponse>({
    queryKey: ['blog-events', 'upcoming', 'landing'],
    queryFn: async () => {
      const res = await fetch(
        `/api/blog/events?type=upcoming&limit=${CAROUSEL_LIMIT}`
      );
      if (!res.ok) throw new Error('Failed to fetch upcoming events');
      return res.json();
    },
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  const pastQuery = useQuery<EventsResponse>({
    queryKey: ['blog-events', 'past', 'landing'],
    queryFn: async () => {
      const res = await fetch('/api/blog/events?type=past&all=1');
      if (!res.ok) throw new Error('Failed to fetch past events');
      return res.json();
    },
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  const featured = featuredQuery.data?.data[0] ?? null;
  const upcoming = upcomingQuery.data?.data ?? [];
  const past = pastQuery.data?.data ?? [];

  // A future highlighted event pins first; a past highlight is just
  // another card in desc order so it never jumps the newest event.
  // ponytail: Date.now() compare, no extra server round-trip.
  const pinned =
    featured && +new Date(featured.startAt) >= Date.now() ? featured : null;
  const seen = new Set<string>(pinned ? [pinned.id] : []);
  const rest = [...upcoming, ...past, ...(featured && !pinned ? [featured] : [])]
    .filter((event) => (seen.has(event.id) ? false : (seen.add(event.id), true)))
    .sort((a, b) => +new Date(b.startAt) - +new Date(a.startAt));
  const events = [...(pinned ? [pinned] : []), ...rest];

  return {
    events,
    isLoading:
      featuredQuery.isLoading || upcomingQuery.isLoading || pastQuery.isLoading,
  };
}
